import numeral from "numeral";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

import { exportCSVFile } from "../../../utilities/dataUtilities";
import { parse } from "json2csv";
import { getPDFHeader } from "./Common.Generators";
import { thresholdByYear } from "../../../constants/Survey";

export function generateCSV({ survey, datasets, district, school }) {
  let data = [];

  // const selectedOptions = [
  //   {
  //     text: "True",
  //     value: "true",
  //     mappingValue: "true",
  //   },
  //   {
  //     text: "False",
  //     value: "false",
  //     mappingValue: "false",
  //   },
  // ];

  let fields = [
    {
      label: "Category",
      value: "category",
    },
    {
      label: "Question",
      value: "question",
    },
    {
      label: "Sub Question",
      value: "subQuestion",
    },
    {
      label: "Option",
      value: "option",
    },
  ];

  let datasetCountProp = (key) => `${key}_count`;
  let datasetTotalProp = (key) => `${key}_total`;
  let datasetPercentageProp = (key) => `${key}_percentage`;

  for (let key in datasets) {
    if (!datasets[key]) continue;

    fields.push({
      label: `${key} Option Percentage`,
      value: datasetPercentageProp(key),
    });

    fields.push({
      label: `${key} Option Count`,
      value: datasetCountProp(key),
    });

    fields.push({
      label: `${key} Question Response Count`,
      value: datasetTotalProp(key),
    });
  }

  for (let i = 0; i < survey.categories.length; i++) {
    let category = survey.categories[i];

    for (let ii = 0; ii < category.questions.length; ii++) {
      let question = category.questions[ii];
      if (isPrivate(question)) continue;

      if (question.children) {
        let questionOptions = question.options;

        if (questionOptions.length === 0) {
          questionOptions = [];

          question.children.forEach((child, i) => {
            questionOptions.push({
              text: child.text,
              value: i,
              mappingValue: i,
            });
          });
        }
        for (let iii = 0; iii < question.children.length; iii++) {
          let child = question.children[iii];
          let rows = getComparisonRows(
            question,
            child.key,
            questionOptions
          ).map((x) => {
            x.category = category.displayName;
            x.question = `${question.questionNumber} ${question.text}`;
            x.subQuestion = `${question.questionNumber}.${child.questionNumber} ${child.text}`;
            return x;
          });

          data = data.concat(rows);
        }
      } else {
        let rows = getComparisonRows(
          question,
          question.key,
          question.options
        ).map((x) => {
          x.category = category.displayName;
          x.question = `${question.questionNumber} ${question.text}`;
          x.subQuestion = "";
          return x;
        });
        
        data = data.concat(rows);
      }
    }
  }

  function getComparisonRows(question, key, options) {
    let questionDataset = getQuestionDatasets(question, key, options, datasets);
    let rows = [];

    for (let i = 0; i < options.length; i++) {
      let row = {};
      let option = options[i];
      row["option"] = option.text;

      for (let datasetKey in datasets) {
        if (datasets[datasetKey]) {
          if (questionDataset[datasetKey]) {

            row[datasetCountProp(datasetKey)] = getOptionTotal(
              questionDataset[datasetKey],
              option
            );

            row[datasetTotalProp(datasetKey)] =
              questionDataset[datasetKey].total;
            row[datasetPercentageProp(datasetKey)] = numeral(
              getOptionPercentage(questionDataset[datasetKey], option)
            ).format("0.00%");
          } else {
            row[datasetCountProp(datasetKey)] = null;

            row[datasetTotalProp(datasetKey)] = null;

            row[datasetPercentageProp(datasetKey)] = null;
          }
        }
      }

      rows.push(row);
    }

    return rows;
  }

  try {
    const csv = parse(data, { fields });
    let year = new Date(survey.startDate).getFullYear();
    let fileName = `${year}_TeacherWorkingConditionSurvey_IndividualItemAnalysis`;

    if (district) {
      fileName += `_${district.id}`;
    }

    if (school) {
      fileName += `_${school.id}`;
    }

    let prependedText = `"${year} Teacher Working Condition Survey - Individual Item Analysis",\n`;
    prependedText += `"District: ${
      district ? district.name : "All Districts"
    }",\n`;
    prependedText += `"School: ${school ? school.name : "All Schools"}",\n\n`;

    exportCSVFile(prependedText + csv, fileName);
  } catch (err) {
    console.error(err);
  }
}

export async function generatePDF({ survey, datasets, district, school }) {
  let content = [];

  // Header
  content.push(
    await getPDFHeader(
      `${new Date(survey.startDate).getFullYear()} Individual Item Analysis`,
      district,
      school
    )
  );
  content.push({ text: " ", style: "header" });

  // Survey Data
  for (let i = 0; i < survey.categories.length; i++) {
    let category = survey.categories[i];
    let categoryContent = {
      text: category.displayName,
      style: "subheader",
      breakAfter: false,
    };

    for (let ii = 0; ii < category.questions.length; ii++) {
      let question = category.questions[ii];

      let questionOptions = question.options;

      if (isPrivate(question)) continue;

      let questionContent = {
        text: `${question.questionNumber} ${question.text}`,
        style: "question",
        breakAfter: false,
      };

      if (question.children) {
        if (questionOptions.length === 0) {
          questionOptions = [];

          question.children.forEach((child, i) => {
            questionOptions.push({
              text: child.text,
              value: i,
              mappingValue: i,
            });
          });
        }
        if (question.type === "checklist") {
          let stack = [];
          if (ii === 0) stack.push(categoryContent);

          stack.push({
            text: question.text,
            style: "subQuestion",
          });

          stack.push(
            genComparisonTableContent(question, question.key, questionOptions)
          );

          content.push({ unbreakable: false, stack });
        } else {
          for (let iii = 0; iii < question.children.length; iii++) {
            let child = question.children[iii];
            let stack = [];

            if (ii === 0) stack.push(categoryContent);
            if (iii === 0) stack.push(questionContent);

            stack.push({
              text: `${child.questionNumber}. ${child.text}`,
              style: "subQuestion",
            });

            stack.push(
              genComparisonTableContent(question, child.key, questionOptions)
            );
            
            content.push({ unbreakable: false, stack });
          }
        }
      } else {
        let stack = [];

        if (ii === 0) stack.push(categoryContent);
        stack.push({
          text: `${question.questionNumber} ${question.text}`,
          style: "question",
        });

        stack.push(
          genComparisonTableContent(question, question.key, question.options)
        );

        content.push({ unbreakable: true, stack });
      }
    }
  }

  pdfMake
    .createPdf({
      content,
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        subTitle: {
          fontSize: 11,
        },
        subheader: {
          fontSize: 16,
          bold: true,
          margin: [0, 10, 0, 5],
        },
        question: {
          fontSize: 11,
          margin: [0, 5, 0, 2],
        },
        subQuestion: {
          fontSize: 10,
          margin: [10, 20, 2, 5],
        },
        comparisonTable: {
          fontSize: 10,
          margin: [0, 2, 10, 20],
          noBorders: true,
        },
        comparisonKey: {
          bold: true,
          alignment: "center",
          margin: [10, 5, 10, 5],
        },
        comparisonValue: {
          alignment: "right",
          margin: [10, 5, 10, 5],
        },
        comparisonOption: {
          margin: [5, 5, 20, 5],
        },
        countRowKey: {
          bold: true,
          margin: [5, 5, 20, 5],
        },
        countRowColumn: {
          alignment: "right",
          margin: [10, 5, 10, 5],
        },
        dividerCol: {
          margin: [0, 0.5, 0, 0],
          fillColor: "#666",
        },
      },
    })
    .open();

  function genComparisonTableContent(question, key, options) {
    let questionDataset = getQuestionDatasets(question, key, options, datasets);

    let header = [""];
    let widths = ["*"];

    let countRow = [
      {
        text: "Count",
        style: "countRowKey",
      },
    ];

    for (let datasetKey in datasets) {
      if (datasets[datasetKey]) {
        header.push({ text: datasetKey, style: "comparisonKey" });
        if (questionDataset[datasetKey] && questionDataset[datasetKey].total) {
          countRow.push({
            text: numeral(questionDataset[datasetKey].total).format("0,0"),
            style: "countRowColumn",
          });
        } else {
          countRow.push({
            text: "--",
            style: "countRowColumn",
          });
        }

        widths.push(75);
      }
    }

    let body = [header];
    for (let i = 0; i < options.length; i++) {
      let option = options[i];
      let rowColumns = [];
      rowColumns.push({ text: option.text, style: "comparisonOption" });

      for (let datasetKey in datasets) {
        if (datasets[datasetKey]) {
          if (
            questionDataset[datasetKey] &&
            questionDataset[datasetKey].total
          ) {
            rowColumns.push({
              text: numeral(
                getOptionPercentage(questionDataset[datasetKey], option)
              ).format("0.00%"),
              style: "comparisonValue",
            });
          } else {
            rowColumns.push({
              text:
                !questionDataset[datasetKey] ||
                questionDataset[datasetKey].total == null
                  ? "--"
                  : "--",
              style: "comparisonValue",
            });
          }
        }
      }

      body.push(rowColumns);
    }

    body.push(countRow);

    return {
      style: "comparisonTable",
      table: {
        widths,
        body,
      },
      layout: {
        hLineWidth(i, node) {
          if (i < 2 || i === node.table.body.length - 1) return 1;
          else return 0;
        },
        hLineColor(i) {
          if (i == 1) return "#999";
          else return "#333";
        },
        vLineWidth: () => 0,
        fillColor(rowIndex, node, columnIndex) {
          let grey = "#f2f2f2";
          let blue = "#E8EFFB";
          let intersect = "#CEDAEF";

          if (rowIndex % 2 !== 0) {
            if (columnIndex % 2 === 0) {
              return blue;
            } else {
              return intersect;
            }
          } else {
            if (columnIndex % 2 === 0) {
              return null;
            } else {
              return grey;
            }
          }
        },
      },
      unbreakable: true,
    };
  }
}

export function getQuestionDatasets(question, questionKey, options, datasets) {
  let minResponses = 5;
  if (datasets?.state?.surveyId) {
    minResponses = thresholdByYear.find(
      (x) => x.surveyId === datasets.state.surveyId
    ).minResponses;
  }

  let result = {};

  const getTargetData = (dataSetKey, targetKey = questionKey) => {
    return datasets[dataSetKey].results.find((x) => x.key === targetKey);
  };

  for (let key in datasets) {
    if (datasets[key]) {
      let target = getTargetData(key);

      if (target) {
        switch (question.type) {
          case "checklist": {
            var total = null;
            result[key] = {
              counts: question.children.map((child, i) => {
                var childData = getTargetData(key, child.key);

                if (!total) total = childData?.counts.reduce((sum, item) => sum + item?.value, 0);

                return {
                  key: i,
                  value:
                    childData?.counts.find((x) => x.key === "true")?.value ?? 0,
                };
              }),
              total: total
            };

            break;
          }
          default: {
            result[key] = {
              counts: target.counts.filter((x) =>
                options.some((y) => y.value === x.key)
              ),
            };
            result[key].total = result[key].counts.reduce((prev, curr) => {
              return prev + curr.value;
            }, 0);
          }
        }

        if (result[key].total < minResponses) {
          result[key] = undefined;
        }
      }
    }
  }

  return result;
}

export function getOptionPercentage(dataset, option) {
  let result = dataset.counts.find((x) => x.key == option.value);
  if (!result) return 0;

  return result.value / dataset.total;
}

export function getOptionTotal(dataset, option) {
  let result = dataset.counts.find((x) => x.key == option.value);
  if (!result) return 0;
  return result.value;
}

export function getKeyCount(obj, includeEmpty = true) {
  let count = 0;

  for (let key in obj) {
    if (includeEmpty == true || (obj[key] && includeEmpty == false)) count++;
  }

  return count;
}

export function isPrivate(question) {
  return question.settings.some(
    (x) => x.key === "private" && x.value == "true"
  );
}
